import { ChannelIcon, Empty, ErrorState, Loading, PageHead, Select, useFormat } from '@ly360/ui';
import { useQuery } from '@tanstack/react-query';
import { Download, Users, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import type { PersonRow } from '../crm/data';
import { NewPersonDialog } from '../crm/forms';
import { api, download } from '../lib/api';
import { useAuth } from '../lib/auth';

/** Identity types mapped to the channel they come from, for the channel icon column. */
export const IDENTITY_CHANNEL: Record<string, string> = {
  phone: 'voice',
  email: 'email',
  wa_bsuid: 'whatsapp',
  ig_user_id: 'instagram',
  fb_psid: 'facebook',
  tt_user_id: 'tiktok',
  snap_user_id: 'snapchat',
  x_user_id: 'x',
  linkedin_urn: 'linkedin',
  web_user_id: 'webchat',
};

const STAGE_CLASS: Record<string, string> = {
  lead: 'badge',
  contact: 'badge-neutral',
  customer: 'badge-success',
  churned: 'badge-warning',
};

export function PeoplePage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const fmt = useFormat();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [stage, setStage] = useState('');
  const [channel, setChannel] = useState('');
  const [marketing, setMarketing] = useState('');
  const [open, setOpen] = useState(false);
  const [exportError, setExportError] = useState<unknown>(null);
  const filters = { channel, marketing: channel ? marketing : '' };
  const params = new URLSearchParams(Object.entries({ q, stage, ...filters }).filter(([, v]) => v));
  const exportParams = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
  const list = useQuery({
    queryKey: ['people', q, stage, channel, marketing],
    queryFn: () => api<{ items: PersonRow[] }>(`/v1/people?${params}`),
  });

  return (
    <>
      <PageHead
        eyebrow={t('nav.main')}
        title={t('crm.people.title')}
        subtitle={t('crm.people.subtitle')}
        actions={
          <div className="quick-actions">
            {can('persons:write') && (
              <Link className="btn" to="/people/duplicates" data-testid="duplicates-link">
                <Users size={16} />
                {t('contacts.duplicates.open')}
              </Link>
            )}
            {can('persons:export') && (
              <button
                className="btn"
                data-testid="export-contacts"
                onClick={() => {
                  setExportError(null);
                  download(`/v1/people/export.csv?${exportParams}`, 'contacts.csv').catch(setExportError);
                }}
              >
                <Download size={16} />
                {t('contacts.export')}
              </button>
            )}
            {can('persons:write') && (
              <button className="btn btn-grad" onClick={() => setOpen(true)} data-testid="add-person">
                <UserPlus size={16} />
                {t('crm.people.new')}
              </button>
            )}
          </div>
        }
      />
      <section className="card">
        <div className="toolbar">
          <input
            className="input"
            type="search"
            placeholder={t('crm.people.search')}
            aria-label={t('common.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Select
            value={stage}
            onChange={setStage}
            aria-label={t('crm.people.col.stage')}
            data-testid="stage-filter"
            options={[
              { value: '', label: t('crm.people.allStages') },
              ...(['lead', 'contact', 'customer', 'churned'] as const).map((s) => ({
                value: s,
                label: t(`crm.lifecycle.${s}`),
              })),
            ]}
          />
          <Select
            value={channel}
            onChange={setChannel}
            aria-label={t('contacts.reachableOn')}
            data-testid="channel-filter"
            options={[
              { value: '', label: t('contacts.anyChannel') },
              ...(['whatsapp', 'email', 'sms'] as const).map((c) => ({
                value: c,
                label: t('contacts.reachable', { channel: t(`channels.${c}`) }),
                icon: <ChannelIcon channel={c} size={11} />,
              })),
            ]}
          />
          <Select
            value={marketing}
            disabled={!channel}
            onChange={setMarketing}
            aria-label={t('contacts.marketingConsent')}
            data-testid="consent-filter"
            options={[
              { value: '', label: t('contacts.anyConsent') },
              ...(['granted', 'withdrawn', 'unknown'] as const).map((c) => ({
                value: c,
                label: t(`contacts.consent.${c}`),
              })),
            ]}
          />
        </div>
        {!!exportError && <ErrorState error={exportError} />}
        {list.isLoading && <Loading />}
        {list.isError && <ErrorState error={list.error} />}
        {list.data?.items.length === 0 && <Empty text={t('crm.people.empty')} />}
        {!!list.data?.items.length && (
          <div className="table-wrap">
            <table className="table table-responsive" data-testid="people-table">
              <thead>
                <tr>
                  <th>{t('crm.people.col.name')}</th>
                  <th>{t('crm.people.col.company')}</th>
                  <th>{t('crm.people.col.stage')}</th>
                  <th>{t('crm.people.col.channels')}</th>
                  <th>{t('contacts.marketingConsent')}</th>
                  <th>{t('crm.people.col.lastContact')}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((p) => (
                  <tr key={p.id} onClick={() => navigate(`/people/${p.id}`)} style={{ cursor: 'pointer' }}>
                    <td data-label={t('crm.people.col.name')}>
                      <Link to={`/people/${p.id}`} onClick={(e) => e.stopPropagation()}>
                        <strong dir="auto">{p.displayName}</strong>
                      </Link>
                      {p.jobTitle && (
                        <div className="faint" style={{ fontSize: 'var(--t-xs)' }}>
                          {p.jobTitle}
                        </div>
                      )}
                    </td>
                    <td data-label={t('crm.people.col.company')} dir="auto">
                      {p.accountName ?? '—'}
                    </td>
                    <td data-label={t('crm.people.col.stage')}>
                      <span className={`badge ${STAGE_CLASS[p.lifecycleStage]}`}>
                        {t(`crm.lifecycle.${p.lifecycleStage}`)}
                      </span>
                    </td>
                    <td data-label={t('crm.people.col.channels')}>
                      <span className="chips">
                        {[
                          ...new Set((p.identityTypes ?? []).map((i) => IDENTITY_CHANNEL[i] ?? 'simulator')),
                        ].map((ch) => (
                          <span key={ch} className="channel-badge" title={t(`channels.${ch}`)}>
                            <ChannelIcon channel={ch} size={12} />
                          </span>
                        ))}
                      </span>
                    </td>
                    <td data-label={t('contacts.marketingConsent')}>
                      <ConsentChips consent={p.marketingConsent} />
                    </td>
                    <td data-label={t('crm.people.col.lastContact')} className="faint">
                      {p.lastMessageAt ? fmt.relative(p.lastMessageAt) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {open && <NewPersonDialog open={open} onClose={() => setOpen(false)} />}
    </>
  );
}

/** Marketing consent per channel: shown only where it was recorded (granted or withdrawn). */
export function ConsentChips({ consent }: { consent?: PersonRow['marketingConsent'] }) {
  const { t } = useTranslation();
  const known = Object.entries(consent ?? {}).filter(([, v]) => v) as [string, 'granted' | 'withdrawn'][];
  if (!known.length) return <span className="faint">—</span>;
  return (
    <span className="chips">
      {known.map(([ch, st]) => (
        <span
          key={ch}
          className={`badge ${st === 'granted' ? 'badge-success' : 'badge-danger'}`}
          title={t(`contacts.consent.${st}`)}
          data-consent={`${ch}:${st}`}
        >
          <ChannelIcon channel={ch} size={11} />
          {t(`contacts.consentShort.${st}`)}
        </span>
      ))}
    </span>
  );
}
